import { inject, PLATFORM_ID } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { filter, map, take } from 'rxjs';
import { AuthService } from '../auth/auth.service';

export const authGuard: CanActivateFn = () => {
    const auth = inject(AuthService);
    const router = inject(Router);
    const platformId = inject(PLATFORM_ID);

    // On server, we can't check auth, so we allow navigation to prevent
    // redirecting to login (which causes a flash of login page on client).
    // The client will re-check auth upon hydration and redirect if needed.
    if (!isPlatformBrowser(platformId)) {
        return true;
    }

    return toObservable(auth.isLoading).pipe(
        filter((loading) => !loading),
        take(1),
        map(() => {
            if (auth.isAuthenticated()) {
                return true;
            }
            return router.createUrlTree(['/login']);
        })
    );
};

export const roleGuard = (...requiredRoles: string[]): CanActivateFn => {
    return () => {
        const auth = inject(AuthService);
        const router = inject(Router);

        return toObservable(auth.isLoading).pipe(
            filter((loading) => !loading),
            take(1),
            map(() => {
                if (!auth.isAuthenticated()) {
                    return router.createUrlTree(['/login']);
                }

                if (!auth.hasAnyRole(...requiredRoles)) {
                    return router.createUrlTree(['/unauthorized']);
                }

                return true;
            })
        );
    };
};
